import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { 
  User, Building2, Upload, Loader2, Mail, Phone, MapPin,
  Shield, CheckCircle2, Clock, AlertCircle,
  AlertTriangle, FileText, Crown, Check, Edit, X, Save, CreditCard, ExternalLink
} from "lucide-react";

export default function Profile() {
  const { user, token, isAdmin } = useAuth();
  const [account, setAccount] = useState(null);
  const [locations, setLocations] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [accountRes, locationsRes, campaignsRes] = await Promise.all([
        axios.get(`${API}/accounts/me`, { headers }),
        axios.get(`${API}/locations`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/campaigns`, { headers }).catch(() => ({ data: [] }))
      ]);
      setAccount(accountRes.data);
      setLocations(locationsRes.data || []);
      setCampaigns(campaignsRes.data || []);
    } catch (error) {
      console.error("Failed to fetch profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error("File size must be less than 2MB");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("logo", file);

      await axios.post(`${API}/accounts/me/logo`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      toast.success("Logo uploaded successfully");
      fetchProfile();
    } catch (error) {
      toast.error("Failed to upload logo");
    } finally {
      setUploading(false);
    }
  };

  const handleSaveChanges = async () => {
    // Build update payload with only changed fields
    const updateData = {};
    
    if (editForm.business_name !== account.business_name) {
      updateData.business_name = editForm.business_name;
    }
    if (editForm.street_address !== account.street_address) {
      updateData.street_address = editForm.street_address;
    }
    if (editForm.suburb !== account.suburb) {
      updateData.suburb = editForm.suburb;
    }
    if (editForm.state !== account.state) {
      updateData.state = editForm.state;
    }
    if (editForm.postcode !== account.postcode) {
      updateData.postcode = editForm.postcode;
    }
    
    // Check primary contact changes
    const currentContact = account.primary_contact || {};
    const editContact = editForm.primary_contact || {};
    if ((editContact.name || "") !== (currentContact.name || "") ||
        (editContact.email || "") !== (currentContact.email || "") ||
        (editContact.phone || "") !== (currentContact.phone || "")) {
      updateData.primary_contact = {
        name: editForm.primary_contact.name || null,
        email: editForm.primary_contact.email || null,
        phone: editForm.primary_contact.phone || null,
      };
    }

    // Check emergency contact changes
    const currentEmergency = account.emergency_contacts?.[0] || {};
    const editEmergency = editForm.emergency_contact || {};
    if ((editEmergency.name || "") !== (currentEmergency.name || "") ||
        (editEmergency.email || "") !== (currentEmergency.email || "") ||
        (editEmergency.phone || "") !== (currentEmergency.phone || "")) {
      updateData.emergency_contact = {
        name: editForm.emergency_contact.name || null,
        email: editForm.emergency_contact.email || null,
        phone: editForm.emergency_contact.phone || null,
      };
    }

    if (editForm.accounts_payable_email !== account.accounts_payable_email) {
      updateData.accounts_payable_email = editForm.accounts_payable_email || null;
    }
    
    if (Object.keys(updateData).length === 0) {
      toast.info("No changes detected");
      setIsEditing(false);
      return;
    }
    
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.put(`${API}/accounts/me`, updateData, { headers });
      
      setAccount(response.data);
      
      // Check if email verification is pending
      if (response.data._email_verification_pending) {
        toast.info(response.data._email_verification_message, { duration: 8000 });
      } else {
        toast.success("Profile updated successfully!");
      }
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to update profile:", error);
      const data = error.response?.data;
      if (error.response?.status === 422 && Array.isArray(data?.detail)) {
        const messages = data.detail.map((err) => err.msg).join(". ");
        toast.error(messages || "Please check your input and try again");
      } else {
        const message = typeof data?.detail === "string" ? data.detail : "Failed to update profile";
        toast.error(message);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleManagePaymentMethods = async () => {
    setPaymentMethodsLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const returnUrl = `${window.location.origin}/billing`;
      const response = await axios.post(`${API}/subscriptions/manage-payment-methods`, {
        return_url: returnUrl
      }, { headers });
      if (response.data.portal_url) {
        window.open(response.data.portal_url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to open payment management");
    } finally {
      setPaymentMethodsLoading(false);
    }
  };

  const startEditing = () => {
    setEditForm({
      business_name: account?.business_name || "",
      street_address: account?.street_address || "",
      suburb: account?.suburb || "",
      state: account?.state || "",
      postcode: account?.postcode || "",
      primary_contact: {
        name: account?.primary_contact?.name || "",
        email: account?.primary_contact?.email || "",
        phone: account?.primary_contact?.phone || ""
      },
      emergency_contact: {
        name: account?.emergency_contacts?.[0]?.name || "",
        email: account?.emergency_contacts?.[0]?.email || "",
        phone: account?.emergency_contacts?.[0]?.phone || ""
      },
      accounts_payable_email: account?.accounts_payable_email || ""
    });
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditForm({});
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      ACTIVE: { color: "bg-green-500/10 text-green-500 border-green-500/30", icon: CheckCircle2, label: "Active" },
      TRIAL: { color: "bg-blue-500/10 text-blue-500 border-blue-500/30", icon: Clock, label: "Trial" },
      PENDING_VERIFICATION: { color: "bg-yellow-500/10 text-yellow-500 border-yellow-500/30", icon: Clock, label: "Pending Verification" },
      SUSPENDED: { color: "bg-red-500/10 text-red-500 border-red-500/30", icon: AlertCircle, label: "Suspended" },
      INACTIVE: { color: "bg-gray-500/10 text-gray-500 border-gray-500/30", icon: AlertCircle, label: "Inactive" }
    };
    const config = statusConfig[status] || statusConfig.INACTIVE;
    const Icon = config.icon;
    return (
      <Badge variant="outline" className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const getTierBadge = (tier) => {
    const tierConfig = {
      GOLD: { bg: "bg-yellow-500", label: "Gold" },
      SILVER: { bg: "bg-gray-400", label: "Silver" },
      BRONZE: { bg: "bg-amber-700", label: "Bronze" }
    };
    const config = tierConfig[tier] || tierConfig.BRONZE;
    return (
      <Badge className={`${config.bg} text-white`}>
        {config.label}
      </Badge>
    );
  };

  const getPaymentMethodLabel = (method) => {
    const methods = {
      credit_card: "Credit Card",
      direct_debit: "Direct Debit",
      paypal: "PayPal",
      terms: "Payment Terms"
    };
    return methods[method] || method || "Not set";
  };

  const handleUpgrade = async (targetTier) => {
    setUpgradeLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const originUrl = window.location.origin;
      
      const response = await axios.post(`${API}/subscriptions/create-checkout`, {
        target_tier: targetTier,
        origin_url: originUrl
      }, { headers });
      
      // Redirect to Stripe Checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error("Failed to create checkout:", error);
      toast.error(error.response?.data?.detail || "Failed to start checkout. Please try again.");
    } finally {
      setUpgradeLoading(false);
    }
  };

  const formatPhone = (phone) => {
    if (!phone) return "Not set";
    return phone;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isAdmin) {
    return (
      <div className="space-y-6 animate-fade-in">
        <h1 className="text-4xl font-black tracking-tight">Profile</h1>
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <p className="text-muted-foreground">Admin accounts do not have an advertiser profile.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const primaryContact = account?.primary_contact;
  const emergencyContacts = account?.emergency_contacts || [];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="profile-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-black tracking-tight">Profile</h1>
          <p className="text-muted-foreground mt-1">
            Manage your account and business information
          </p>
        </div>
        {!isEditing ? (
          <Button 
            variant="outline" 
            onClick={startEditing}
          >
            <Edit className="mr-2 h-4 w-4" />
            Edit Profile
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button 
              variant="ghost" 
              onClick={cancelEditing}
              disabled={saving}
            >
              <X className="mr-2 h-4 w-4" />
              Cancel
            </Button>
            <Button 
              onClick={handleSaveChanges}
              disabled={saving}
              data-testid="save-profile-btn"
            >
              {saving ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</>
              ) : (
                <><Save className="mr-2 h-4 w-4" />Save Changes</>
              )}
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Account Overview */}
        <div className="space-y-6">
          {/* Account Status Card */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Account Status</CardTitle>
                {account && getStatusBadge(account.status)}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Account Type</span>
                <Badge className="bg-primary/10 text-primary border-primary/30">Business</Badge>
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Member Since</span>
                <span className="text-sm font-medium">
                  {account?.created_at ? new Date(account.created_at).toLocaleDateString() : 'N/A'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Account ID</span>
                <span className="text-xs font-mono text-muted-foreground">{account?.id?.slice(0, 8)}...</span>
              </div>
            </CardContent>
          </Card>

          {/* Company Logo Card */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Company Logo</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col items-center gap-4">
                <div className="w-32 h-32 rounded-xl border-2 border-dashed border-border flex items-center justify-center overflow-hidden bg-muted/30">
                  {account?.logo_url ? (
                    <img src={account.logo_url} alt="Logo" className="w-full h-full object-contain" />
                  ) : (
                    <Building2 className="h-12 w-12 text-muted-foreground" />
                  )}
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleLogoUpload}
                  accept="image/*"
                  className="hidden"
                />
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Uploading...</>
                  ) : (
                    <><Upload className="mr-2 h-4 w-4" />Upload Logo</>
                  )}
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                  Square image recommended<br/>Max 2MB
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Active Campaigns Summary */}
          <Card className="bg-card border-border" data-testid="active-campaigns-card">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Crown className="h-5 w-5 text-amber-500" />
                <CardTitle className="text-lg">Active Campaigns</CardTitle>
              </div>
              <CardDescription>Your current campaign summary</CardDescription>
            </CardHeader>
            <CardContent>
              {campaigns.length > 0 ? (
                <div className="space-y-3">
                  {/* Tier counts */}
                  {(() => {
                    const tierCounts = campaigns.reduce((acc, c) => {
                      const t = (c.tier || 'BRONZE').toUpperCase();
                      acc[t] = (acc[t] || 0) + 1;
                      return acc;
                    }, {});
                    return (
                      <div className="flex gap-3 mb-1">
                        {tierCounts.GOLD > 0 && (
                          <span className="text-xs font-medium text-yellow-500">Gold ({tierCounts.GOLD})</span>
                        )}
                        {tierCounts.SILVER > 0 && (
                          <span className="text-xs font-medium text-gray-400">Silver ({tierCounts.SILVER})</span>
                        )}
                        {tierCounts.BRONZE > 0 && (
                          <span className="text-xs font-medium text-orange-700">Bronze ({tierCounts.BRONZE})</span>
                        )}
                      </div>
                    );
                  })()}
                  {campaigns.map((c) => (
                    <div key={c.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border/50">
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{c.name}</p>
                        <Badge variant="outline" className={`text-xs mt-1 ${
                          c.status === 'ACTIVE' ? 'border-green-500/30 text-green-500' :
                          c.status === 'PAUSED' ? 'border-amber-500/30 text-amber-500' :
                          c.status === 'DRAFT' ? 'border-blue-500/30 text-blue-500' :
                          'border-muted-foreground/30'
                        }`}>
                          {c.status}
                        </Badge>
                      </div>
                      {getTierBadge(c.tier)}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No active campaigns</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Middle Column - Business & Address */}
        <div className="space-y-6">
          {/* Business Information */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">Business Details</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs text-muted-foreground uppercase tracking-wider">Business Name</Label>
                {isEditing ? (
                  <Input 
                    value={editForm.business_name || ""} 
                    onChange={(e) => setEditForm({...editForm, business_name: e.target.value})}
                    className="mt-1"
                  />
                ) : (
                  <p className="font-semibold mt-1">{account?.business_name || 'Not set'}</p>
                )}
              </div>
              <Separator />
              <div>
                <Label className="text-xs text-muted-foreground uppercase tracking-wider">ABN</Label>
                <p className="font-mono mt-1">{account?.abn || 'Not set'}</p>
                {isEditing && (
                  <p className="text-xs text-muted-foreground mt-1">ABN cannot be changed via this form</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Head Office Address */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">Head Office Address</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {isEditing ? (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">Street Address</Label>
                    <Input 
                      value={editForm.street_address || ""} 
                      onChange={(e) => setEditForm({...editForm, street_address: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Suburb</Label>
                      <Input 
                        value={editForm.suburb || ""} 
                        onChange={(e) => setEditForm({...editForm, suburb: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">State</Label>
                      <Input 
                        value={editForm.state || ""} 
                        onChange={(e) => setEditForm({...editForm, state: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Postcode</Label>
                    <Input 
                      value={editForm.postcode || ""} 
                      onChange={(e) => setEditForm({...editForm, postcode: e.target.value})}
                      className="mt-1 w-32"
                    />
                  </div>
                </div>
              ) : (
                <div className="p-4 rounded-lg bg-muted/30 border border-border">
                  {account?.street_address ? (
                    <div className="space-y-1">
                      <p className="font-medium">{account.street_address}</p>
                      <p className="text-muted-foreground">
                        {account.suburb}, {account.state} {account.postcode}
                      </p>
                      <p className="text-muted-foreground">{account.country || 'Australia'}</p>
                    </div>
                  ) : (
                    <p className="text-muted-foreground">No address on file</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Operating Locations - mirrors Locations page */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-primary" />
                  <CardTitle className="text-lg">Operating Locations</CardTitle>
                </div>
                <Badge variant="outline" className="text-xs">{locations.length} location{locations.length !== 1 ? 's' : ''}</Badge>
              </div>
              <CardDescription>Business locations managed in the Locations section</CardDescription>
            </CardHeader>
            <CardContent>
              {locations.length > 0 ? (
                <div className="space-y-3">
                  {locations.map((loc, i) => (
                    <div key={loc.id || i} className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 border border-border/50">
                      <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <p className="font-medium text-sm">{loc.name}</p>
                        {loc.street_address && (
                          <p className="text-xs text-muted-foreground mt-0.5">{loc.street_address}</p>
                        )}
                        {(loc.suburb || loc.state || loc.postcode) && (
                          <p className="text-xs text-muted-foreground">
                            {[loc.suburb, loc.state, loc.postcode].filter(Boolean).join(', ')}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No locations added yet. Add locations in the Locations section.</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Contacts */}
        <div className="space-y-6">
          {/* User Login Info */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <User className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">Login Credentials</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{user?.email}</span>
              </div>
              <div className="flex items-center gap-3">
                <Shield className="h-4 w-4 text-muted-foreground" />
                <Badge variant="outline" className="text-xs">
                  {user?.role?.replace(/_/g, " ")}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Primary Contact */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <User className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">Primary Contact</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {isEditing ? (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">Name</Label>
                    <Input 
                      value={editForm.primary_contact?.name || ""} 
                      onChange={(e) => setEditForm({
                        ...editForm, 
                        primary_contact: {...editForm.primary_contact, name: e.target.value}
                      })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Email</Label>
                    <Input 
                      type="email"
                      value={editForm.primary_contact?.email || ""} 
                      onChange={(e) => setEditForm({
                        ...editForm, 
                        primary_contact: {...editForm.primary_contact, email: e.target.value}
                      })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Phone</Label>
                    <Input 
                      value={editForm.primary_contact?.phone || ""} 
                      onChange={(e) => setEditForm({
                        ...editForm, 
                        primary_contact: {...editForm.primary_contact, phone: e.target.value}
                      })}
                      className="mt-1"
                    />
                  </div>
                </div>
              ) : (
                <div className="p-4 rounded-lg bg-muted/30 border border-border space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Name</Label>
                    <p className="font-medium">{primaryContact?.name || user?.name || 'Not set'}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{primaryContact?.email || user?.email || 'Not set'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{formatPhone(primaryContact?.phone)}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Emergency Contact */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-orange-500" />
                <CardTitle className="text-lg">Emergency Contact</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {isEditing ? (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">Name</Label>
                    <Input
                      value={editForm.emergency_contact?.name || ""}
                      onChange={(e) => setEditForm({
                        ...editForm,
                        emergency_contact: {...editForm.emergency_contact, name: e.target.value}
                      })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Email</Label>
                    <Input
                      type="email"
                      value={editForm.emergency_contact?.email || ""}
                      onChange={(e) => setEditForm({
                        ...editForm,
                        emergency_contact: {...editForm.emergency_contact, email: e.target.value}
                      })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Phone</Label>
                    <Input
                      value={editForm.emergency_contact?.phone || ""}
                      onChange={(e) => setEditForm({
                        ...editForm,
                        emergency_contact: {...editForm.emergency_contact, phone: e.target.value}
                      })}
                      className="mt-1"
                    />
                  </div>
                </div>
              ) : emergencyContacts.length > 0 ? (
                <div className="p-4 rounded-lg bg-orange-500/5 border border-orange-500/20 space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Name</Label>
                    <p className="font-medium">{emergencyContacts[0].name}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{emergencyContacts[0].email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{formatPhone(emergencyContacts[0].phone)}</span>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No emergency contact on file</p>
              )}
            </CardContent>
          </Card>

          {/* Payment Methods */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">Payment Methods</CardTitle>
              </div>
              <CardDescription>Manage your saved cards and billing details</CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                variant="outline"
                onClick={handleManagePaymentMethods}
                disabled={paymentMethodsLoading}
                className="w-full sm:w-auto"
              >
                {paymentMethodsLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <ExternalLink className="mr-2 h-4 w-4" />
                )}
                Manage Payment Methods
              </Button>
            </CardContent>
          </Card>

          {/* Accounts Contact */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">Accounts Contact</CardTitle>
              </div>
              <CardDescription>Invoices and billing notifications</CardDescription>
            </CardHeader>
            <CardContent>
              {isEditing ? (
                <div>
                  <Label className="text-xs">Accounts Payable Email</Label>
                  <Input
                    type="email"
                    value={editForm.accounts_payable_email || ""}
                    onChange={(e) => setEditForm({...editForm, accounts_payable_email: e.target.value})}
                    className="mt-1"
                  />
                </div>
              ) : (
                <div className="p-4 rounded-lg bg-muted/30 border border-border">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{account?.accounts_payable_email || 'Not set'}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer Note */}
      <Card className="bg-muted/30 border-border">
        <CardContent className="py-4">
          <p className="text-sm text-muted-foreground text-center">
            Click "Edit Profile" above to update your business information. Changes are saved instantly.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
